import React, { useCallback, useEffect, useRef, useState } from "react";
import { ToastIcon } from "./ToastIcon.tsx";
import type { ToastItem as ToastItemType } from "./types.ts";

// ─── Per-type design tokens ───────────────────────────────────────────────────

const TOKENS = {
  success: {
    color:        "#22c55e",
    colorDark:    "#4ade80",
    glow:         "rgba(34,197,94,0.25)",
    bg:           "rgba(34,197,94,0.06)",
    border:       "rgba(34,197,94,0.22)",
    shimmer:      "rgba(34,197,94,0.12)",
    label:        "Success",
  },
  error: {
    color:        "#ef4444",
    colorDark:    "#f87171",
    glow:         "rgba(239,68,68,0.25)",
    bg:           "rgba(239,68,68,0.06)",
    border:       "rgba(239,68,68,0.22)",
    shimmer:      "rgba(239,68,68,0.12)",
    label:        "Error",
  },
  warning: {
    color:        "#f59e0b",
    colorDark:    "#fbbf24",
    glow:         "rgba(245,158,11,0.25)",
    bg:           "rgba(245,158,11,0.06)",
    border:       "rgba(245,158,11,0.22)",
    shimmer:      "rgba(245,158,11,0.12)",
    label:        "Warning",
  },
  info: {
    color:        "#3b82f6",
    colorDark:    "#60a5fa",
    glow:         "rgba(59,130,246,0.25)",
    bg:           "rgba(59,130,246,0.06)",
    border:       "rgba(59,130,246,0.22)",
    shimmer:      "rgba(59,130,246,0.12)",
    label:        "Info",
  },
  loading: {
    color:        "#c9a55c",
    colorDark:    "#d4b06a",
    glow:         "rgba(201,165,92,0.25)",
    bg:           "rgba(201,165,92,0.06)",
    border:       "rgba(201,165,92,0.22)",
    shimmer:      "rgba(201,165,92,0.12)",
    label:        "Loading",
  },
} as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isDarkMode() {
  return document.documentElement.classList.contains("dark");
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  toast: ToastItemType;
  onDismiss: (id: string) => void;
  index: number;
}

export function ToastItem({ toast, onDismiss, index }: Props) {
  const tk = TOKENS[toast.type];
  const dark = isDarkMode();
  const accentColor = dark ? tk.colorDark : tk.color;

  const duration = toast.duration ?? (toast.type === "loading" ? Infinity : 4000);
  const hasProgress = duration !== Infinity;

  // ── Hover state ──
  const [hovered, setHovered] = useState(false);
  const [closeHovered, setCloseHovered] = useState(false);
  const [actionHovered, setActionHovered] = useState(false);

  // ── Progress bar (rAF-driven so we can pause on hover) ──
  const progressBarRef  = useRef<HTMLDivElement>(null);
  const startRef        = useRef(Date.now());
  const elapsedRef      = useRef(0);
  const rafRef          = useRef<number>(0);
  const pausedRef       = useRef(false);

  useEffect(() => {
    if (!hasProgress || !progressBarRef.current) return;
    startRef.current = Date.now();

    function tick() {
      if (pausedRef.current) { rafRef.current = requestAnimationFrame(tick); return; }
      const elapsed = elapsedRef.current + (Date.now() - startRef.current);
      const pct = Math.min(elapsed / (duration as number), 1);
      if (progressBarRef.current) {
        progressBarRef.current.style.transform = `scaleX(${1 - pct})`;
      }
      if (pct < 1) rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [hasProgress, duration]);

  const handleMouseEnter = useCallback(() => {
    setHovered(true);
    if (!hasProgress) return;
    elapsedRef.current += Date.now() - startRef.current;
    pausedRef.current = true;
  }, [hasProgress]);

  const handleMouseLeave = useCallback(() => {
    setHovered(false);
    if (!hasProgress) return;
    startRef.current = Date.now();
    pausedRef.current = false;
  }, [hasProgress]);

  // ── Entrance stagger ──
  const delay = `${index * 55}ms`;

  // ── Surface colors from CSS vars ──
  const surface = dark
    ? "rgba(14,14,18,0.92)"
    : "rgba(255,255,255,0.92)";
  const textPrimary   = dark ? "rgba(255,255,255,0.95)" : "rgba(14,14,16,0.95)";
  const textSecondary = dark ? "rgba(255,255,255,0.5)"  : "rgba(14,14,16,0.5)";

  return (
    <div
      role="alert"
      aria-live={toast.type === "error" ? "assertive" : "polite"}
      aria-atomic="true"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: 360,
        maxWidth: "calc(100vw - 32px)",
        borderRadius: 16,
        overflow: "hidden",
        cursor: "default",
        userSelect: "none",
        // Glass morphism surface
        background: surface,
        backdropFilter: "blur(20px) saturate(180%)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
        border: `1px solid ${tk.border}`,
        // Layered shadow: ambient + colored glow
        boxShadow: hovered
          ? `0 20px 60px rgba(0,0,0,0.22), 0 8px 24px rgba(0,0,0,0.14), 0 0 0 1px ${tk.border}, 0 0 32px ${tk.glow}`
          : `0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px ${tk.border}`,
        transform: hovered ? "translateY(-2px) scale(1.012)" : "translateY(0) scale(1)",
        transition: "box-shadow 0.25s ease, transform 0.25s cubic-bezier(0.34,1.56,0.64,1)",
        animation: toast.isExiting
          ? "toast-out 0.38s cubic-bezier(0.4,0,1,1) forwards"
          : `toast-in 0.5s cubic-bezier(0.16,1,0.3,1) ${delay} both`,
        willChange: "transform, opacity",
      }}
    >
      {/* ── Shimmer sweep on enter ── */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(105deg, transparent 40%, ${tk.shimmer} 50%, transparent 60%)`,
          animation: toast.isExiting ? "none" : `shimmer-slide 0.9s ease ${delay} both`,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* ── Top accent line ── */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
          opacity: 0.9,
          zIndex: 1,
        }}
      />

      {/* ── Main body ── */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          padding: "14px 14px 14px 16px",
        }}
      >
        {/* Icon badge */}
        <div
          aria-hidden="true"
          style={{
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: 10,
            background: `linear-gradient(135deg, ${accentColor}22, ${accentColor}11)`,
            border: `1px solid ${accentColor}33`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: accentColor,
            boxShadow: `0 2px 8px ${tk.glow}`,
            marginTop: 1,
          }}
        >
          <ToastIcon type={toast.type} />
        </div>

        {/* Text content */}
        <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
          {/* Type label */}
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: accentColor,
              marginBottom: 2,
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {tk.label}
          </div>

          {/* Message */}
          <p
            style={{
              margin: 0,
              fontSize: 13.5,
              fontWeight: 600,
              color: textPrimary,
              lineHeight: 1.45,
              letterSpacing: "-0.01em",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {toast.message}
          </p>

          {/* Description */}
          {toast.description && (
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 12,
                fontWeight: 400,
                color: textSecondary,
                lineHeight: 1.55,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {toast.description}
            </p>
          )}

          {/* Action button */}
          {toast.action && (
            <button
              onClick={toast.action.onClick}
              onMouseEnter={() => setActionHovered(true)}
              onMouseLeave={() => setActionHovered(false)}
              style={{
                marginTop: 8,
                padding: "5px 12px",
                fontSize: 11.5,
                fontWeight: 600,
                fontFamily: "Poppins, sans-serif",
                letterSpacing: "0.02em",
                color: dark ? "#fff" : "#fff",
                background: actionHovered
                  ? accentColor
                  : `linear-gradient(135deg, ${accentColor}dd, ${accentColor}aa)`,
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                transition: "all 0.18s ease",
                transform: actionHovered ? "scale(1.04)" : "scale(1)",
                boxShadow: actionHovered ? `0 4px 12px ${tk.glow}` : "none",
              }}
            >
              {toast.action.label}
            </button>
          )}
        </div>

        {/* Close button */}
        {toast.type !== "loading" && (
          <button
            onClick={() => onDismiss(toast.id)}
            onMouseEnter={() => setCloseHovered(true)}
            onMouseLeave={() => setCloseHovered(false)}
            aria-label="Dismiss"
            style={{
              flexShrink: 0,
              width: 24,
              height: 24,
              borderRadius: 8,
              border: "none",
              background: closeHovered
                ? dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"
                : "transparent",
              color: closeHovered ? textPrimary : textSecondary,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              transition: "all 0.15s ease",
              transform: closeHovered ? "scale(1.1) rotate(90deg)" : "scale(1) rotate(0deg)",
              marginTop: -1,
            }}
          >
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
              <path d="M1.5 1.5L9.5 9.5M9.5 1.5L1.5 9.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      {/* ── Progress bar ── */}
      {hasProgress && (
        <div
          aria-hidden="true"
          style={{
            position: "relative",
            zIndex: 1,
            height: 3,
            background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          <div
            ref={progressBarRef}
            style={{
              height: "100%",
              width: "100%",
              transformOrigin: "left center",
              background: `linear-gradient(90deg, ${accentColor}88, ${accentColor})`,
              borderRadius: "0 2px 2px 0",
              boxShadow: `0 0 6px ${tk.glow}`,
            }}
          />
        </div>
      )}
    </div>
  );
}
